import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Eye, Play, Square, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { instances, products } from '../api';
import InstanzAnlegenModal from '../components/InstanzAnlegenModal';
import { umgebung } from '../umgebungen';
import StatusBadge from '../components/StatusBadge';
import { useTabelle, TabellenKopf, Blaetterleiste, SpaltenWahl, type SpaltenDefinition, type FilterDefinition } from "../components/TabellenSteuerung";
import { FarbPunkt } from "../components/Farbe";
import ListenReiter, { nachSicht, type ListenSicht } from "../components/ListenReiter";
import { useEinstellung } from "../contexts/EinstellungenContext";
import StandardsKnopf from "../components/StandardsKnopf";
import type { InstanceStatus, InstanceEnvironment } from '../types';
import { formatDistanceToNow } from 'date-fns';

const INSTANCE_STATUSES: InstanceStatus[] = [
  'PENDING', 'DEPLOYING', 'ACTIVE', 'UPGRADING', 'DOWNGRADING',
  'SUSPENDED', 'ARCHIVED', 'CANCELLATION_SCHEDULED', 'TERMINATED', 'DELETING', 'DELETED', 'FAILED',
];

/**
 * Produktion oder Abnahme, auf einen Blick.
 *
 * Nichts anzuzeigen, wo nichts hinterlegt ist, ist hier die richtige Antwort:
 * ein leeres Feld sagt „unbekannt", eine geratene Marke sagt „Produktion" —
 * und die zweite Aussage haelt jemand fuer belastbar.
 */
function UmgebungsMarke({ wert }: { wert: InstanceEnvironment | null }) {
  const { t } = useTranslation();
  const def = umgebung(wert);
  if (!def) return <span className="text-xs text-slate-300">—</span>;
  return (
    <span
      title={t(`environment.${def.schluessel}Hint`)}
      className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ring-1 ${def.farbe}`}
    >
      {t(`environment.${def.schluessel}`)}
    </span>
  );
}

/** "Node.js 22.21.1" → "Node.js 22" — für den Filter. */
function hauptversion(e: { name: string; version: string }): string {
  return `${e.name} ${e.version.split('.')[0]}`;
}

/**
 * Was in den Containern läuft. Volle Version, nicht nur die Hauptversion: ob
 * eine bekannte Lücke trifft, entscheidet die Patchstufe — und genau dafür
 * soll diese Spalte später rot werden können.
 */
function StackZelle({ eintraege, am }: {
  eintraege?: { name: string; version: string; container: string }[] | null;
  am?: string | null;
}) {
  if (!eintraege?.length) {
    return (
      <span className="text-xs text-slate-300" title={am ? 'Keine bekannte Laufzeit in den Containern' : 'Noch nicht erfasst'}>
        —
      </span>
    );
  }
  const stand = am ? `erfasst ${formatDistanceToNow(new Date(am), { addSuffix: true })}` : '';
  return (
    <div className="flex flex-wrap gap-1">
      {eintraege.map((e) => (
        <span
          key={`${e.name}-${e.version}`}
          title={`${e.name} ${e.version} — Container ${e.container}${stand ? `, ${stand}` : ''}`}
          className="whitespace-nowrap rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-600"
        >
          {e.name} {e.version}
        </span>
      ))}
    </div>
  );
}

/** Wann zuletzt etwas an der Instanz geändert wurde, und von wem. */
function AenderungsZelle({ aenderung }: {
  aenderung?: { am: string; aktion: string; wer: string | null } | null;
}) {
  if (!aenderung) return <span className="text-slate-300">—</span>;
  // Aufträge über einen API-Schlüssel stehen als "apikey:<name>" im Protokoll.
  const wer = aenderung.wer?.startsWith('apikey:')
    ? `API: ${aenderung.wer.slice('apikey:'.length)}`
    : (aenderung.wer ?? 'unbekannt');
  return (
    <div title={`${aenderung.aktion} — ${new Date(aenderung.am).toLocaleString('de-DE')}`}>
      <div className="text-slate-500">{formatDistanceToNow(new Date(aenderung.am), { addSuffix: true })}</div>
      <div className="text-slate-400">{wer}</div>
    </div>
  );
}

/**
 * Die Betriebsliste — dreimal dieselbe Seite, dreimal ein anderer Ausschnitt.
 *
 * `art` filtert; ohne Angabe kommen die Produktinstanzen, wie bisher. Eine
 * eigene Seite je Art waere dreimal dieselbe Tabelle mit denselben Filtern,
 * denselben Massenaktionen und denselben Spalten — und nach der zweiten
 * Aenderung an einer davon dreimal eine andere.
 */
export default function InstancesPage({ art = 'PRODUKT' }: { art?: 'PRODUKT' | 'APP' | 'WEBSEITE' } = {}) {
  const [anlegen, setAnlegen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Gemerkt je Person: wer meist „alle" sieht, soll nicht jedes Mal umschalten.
  const [sicht, setSicht] = useEinstellung<ListenSicht>(`instanzen.${art}.sicht`, 'aktiv');
  // Produktreiter (nur bei Produkten): ojfm vorgewählt, danach der zuletzt gewählte.
  const [produktReiter, setProduktReiter] = useEinstellung<string>('instanzen.produktReiter', 'ojfm');
  // Filter und Suche liefen bis hierher als Abfrageparameter ans Backend --
  // und damit an der Tabellensteuerung vorbei, die genau dafuer da ist. Bei
  // vierzehn Instanzen ist eine Abfrage je Tastendruck ohnehin die teurere
  // Loesung. Jetzt einmal laden, im Browser filtern; die Filter stehen im
  // Tabellenkopf unter ihrer Spalte.
  const { data, isLoading } = useQuery({
    queryKey: ['instances', art],
    queryFn: () => instances.list({ art }),
  });

  const { data: productsList } = useQuery({
    queryKey: ['products'],
    queryFn: () => products.list(),
  });

  const startMutation = useMutation({
    mutationFn: (id: string) => instances.start(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instances'] }),
  });

  const stopMutation = useMutation({
    mutationFn: (id: string) => instances.stop(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instances'] }),
  });

  const restartMutation = useMutation({
    mutationFn: (id: string) => instances.restart(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instances'] }),
  });

  const rows = data ?? [];

  // Nur Sortierung: Suche und Filter macht diese Seite schon selbst, und zwar
  // serverseitig — sie würde bei vielen Instanzen sonst zweimal filtern.
  const spalten: SpaltenDefinition<(typeof rows)[number]>[] = [
    // Vor dem Kunden: wer eine Instanz sucht, hat oft die Nummer und nicht
    // den Firmennamen im Kopf.
    { key: 'nummer', label: 'Nr.', wert: (i) => i.nummer ?? 0, fest: true },
    { key: 'kunde', label: t('tables.customer'), wert: (i) => i.customer?.companyName ?? i.customerId, fest: true },
    { key: 'produkt', label: t('tables.product'), wert: (i) => i.product?.name ?? '' },
    { key: 'plan', label: t('tables.plan'), wert: (i) => i.plan?.name ?? '' },
    // Domain zuerst, Subdomain dahinter: So stehen die Instanzen einer Domain
    // beim Sortieren beieinander, statt über die ganze Liste verteilt zu sein.
    // Genau dafür wird nach dieser Spalte sortiert.
    {
      key: 'domain',
      label: t('tables.domain'),
      wert: (i) => [i.domain, i.subdomain].filter(Boolean).join(' ') || '',
    },
    { key: 'status', label: t('common.status'), wert: (i) => i.status },
    { key: 'server', label: t('tables.serverHosting'), wert: (i) => i.server?.hostname ?? '' },
    { key: 'stack', label: 'Tech-Stack', wert: (i) => (i.techStack ?? []).map((e) => `${e.name} ${e.version}`).join(' ') },
    { key: 'umgebung', label: t('environment.label'), wert: (i) => i.environment ?? '' },
    { key: 'deployed', label: t('tables.deployed'), wert: (i) => i.deployedAt },
    { key: 'geaendert', label: 'Zuletzt geändert', wert: (i) => (i.letzteAenderung ? new Date(i.letzteAenderung.am) : null) },
  ];

  /**
   * Was aus dem Betrieb heraus ist.
   *
   * Bewusst nur diese drei: `SUSPENDED` ist eine Pause und `FAILED` ein
   * Problem — beides will man sehen. Abgelegt, beendet und gelöscht dagegen
   * steht sonst zwischen dem, womit man arbeitet.
   */
  const AUSSER_BETRIEB: string[] = ['ARCHIVED', 'TERMINATED', 'DELETED'];
  const istAktiv = (i: (typeof rows)[number]) => !AUSSER_BETRIEB.includes(i.status);

  /**
   * Produktreiter: Alle | OJFM | Bryve | eLibrary | … — was eine Instanz ist,
   * sagt die Application (bei Bündeln verschieden von Instanz zu Instanz
   * desselben Produkts), sonst das Produkt.
   */
  const mitProduktReitern = art === 'PRODUKT';
  const slugVon = (i: (typeof rows)[number]) => i.application?.slug ?? i.product?.slug ?? '';
  const BEKANNT: Record<string, string> = { ojfm: 'OJFM', bryve: 'Bryve', elibrary: 'eLibrary' };
  const weitere = [...new Set(rows.map(slugVon).filter((x) => x && !(x in BEKANNT)))].sort();
  const reiterListe: { slug: string; label: string }[] = [
    { slug: 'alle', label: 'Alle' },
    ...Object.entries(BEKANNT).map(([slug, label]) => ({ slug, label })),
    ...weitere.map((slug) => {
      const i = rows.find((x) => slugVon(x) === slug);
      return { slug, label: i?.application?.name ?? i?.product?.name ?? slug };
    }),
  ];
  const imSicht = (i: (typeof rows)[number]) => sicht === 'alle' || (sicht === 'aktiv') === istAktiv(i);
  const reiterZahl = (slug: string) => rows.filter((i) => imSicht(i) && (slug === 'alle' || slugVon(i) === slug)).length;
  const rowsImReiter = !mitProduktReitern || produktReiter === 'alle' ? rows : rows.filter((i) => slugVon(i) === produktReiter);

  const { gefiltert, zaehler } = nachSicht(rowsImReiter, istAktiv, sicht);

  /**
   * Die Filter, die im Tabellenkopf unter ihrer Spalte stehen.
   *
   * Nur Spalten mit einer ueberschaubaren Zahl von Auspraegungen. Der Kunde ist
   * trotzdem dabei: man arbeitet meist die Instanzen eines Kunden ab, und die
   * Auswahl bleibt bis zum Abwählen gemerkt. Die Domain gehört in die Suche.
   */
  const nurWerte = (w: (string | null | undefined)[]) =>
    [...new Set(w.filter(Boolean) as string[])].sort().map((x) => ({ wert: x, label: x }));

  const filter: FilterDefinition<(typeof rows)[number]>[] = [
    { key: 'kunde', label: t('tables.customer'), optionen: nurWerte(rows.map((i) => i.customer?.companyName)),
      passt: (i, w) => (i.customer?.companyName ?? '') === w },
    { key: 'produkt', label: t('tables.product'), optionen: nurWerte(rows.map((i) => i.product?.name)),
      passt: (i, w) => (i.product?.name ?? '') === w },
    { key: 'status', label: t('common.status'), optionen: nurWerte(rows.map((i) => i.status)),
      passt: (i, w) => i.status === w },
    { key: 'server', label: t('tables.serverHosting'), optionen: nurWerte(rows.map((i) => i.server?.hostname)),
      passt: (i, w) => (i.server?.hostname ?? '') === w },
    // Nach Hauptversion: „wo läuft noch Node.js 20" ist die Frage, nicht
    // „wo läuft 20.18.3".
    { key: 'stack', label: 'Tech-Stack', optionen: nurWerte(rows.flatMap((i) => (i.techStack ?? []).map(hauptversion))),
      passt: (i, w) => (i.techStack ?? []).some((e) => hauptversion(e) === w) },
    { key: 'umgebung', label: t('environment.label'), optionen: nurWerte(rows.map((i) => i.environment)),
      passt: (i, w) => (i.environment ?? '') === w },
  ];

  const tabelle = useTabelle({
    speichern: `instanzen.${art}`,
    zeilen: gefiltert,
    spalten,
    filter,
    suchfelder: (i) => [
      i.nummer != null ? String(i.nummer) : null,
      i.customer?.companyName, i.domain, i.subdomain, i.stackName, i.server?.hostname,
    ],
    startSortierung: { key: 'kunde', richtung: 'auf' },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Kundeninstallationen</p>
        {/* Bis hierher gab es keinen Weg, eine Instanz ueber die Oberflaeche
            anzulegen -- jede entstand ueber eine Bestellung oder von Hand am
            Server. Fuer eine Abnahmeinstanz ohne Bestellung fehlte damit alles. */}
        <div className="flex items-center gap-2">
          <SpaltenWahl spalten={spalten} steuerung={tabelle} />
          <StandardsKnopf bereich="instanz" />
          <button
            onClick={() => setAnlegen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            <Plus size={15} /> Instanz anlegen
          </button>
        </div>
      </div>

      {anlegen && <InstanzAnlegenModal art={art} onClose={() => setAnlegen(false)} />}

      {mitProduktReitern && (
        <div role="tablist" aria-label="Produkt" className="flex flex-wrap gap-1 border-b border-slate-200">
          {reiterListe.map((r) => (
            <button
              key={r.slug}
              type="button"
              role="tab"
              aria-selected={produktReiter === r.slug}
              onClick={() => setProduktReiter(r.slug)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm ${
                produktReiter === r.slug
                  ? 'border-brand-600 font-semibold text-brand-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {r.label}
              <span className={`ml-1.5 tabular-nums ${produktReiter === r.slug ? 'text-brand-500' : 'text-slate-400'}`}>
                {reiterZahl(r.slug)}
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-surface">
        <ListenReiter sicht={sicht} onWechsel={setSicht} zaehler={zaehler} />
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <TabellenKopf spalten={spalten} steuerung={tabelle} filter={filter} extra={[t('common.actions')]} />
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  {Array.from({ length: tabelle.sichtbareSpalten.length + 1 }).map((_, j) => (
                    <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-200 rounded" /></td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={tabelle.sichtbareSpalten.length + 1} className="px-4 py-10 text-center text-slate-400 italic">{t('empty.instances')}</td>
              </tr>
            ) : (
              tabelle.sichtbar.map((inst) => (
                <tr key={inst.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-right font-mono text-xs tabular-nums text-slate-400">
                    {inst.nummer ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-700 font-medium">
                    <span className="inline-flex items-center gap-2">
                      <FarbPunkt farbe={inst.farbe} titel={inst.stackName ?? undefined} />
                      {inst.customer?.companyName ?? inst.customerId.slice(0, 8)}
                    </span>
                  </td>
                  {tabelle.zeigt('produkt') && (
                    <td className="px-4 py-3 text-slate-600 text-xs">
                      {inst.product?.name ?? inst.productId.slice(0, 8)}
                    </td>
                  )}
                  {tabelle.zeigt('plan') && (
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {inst.plan?.name ?? inst.planId?.slice(0, 8) ?? '—'}
                    </td>
                  )}
                  {/*
                    Subdomain davor, gedämpft. Ohne sie standen zwei Instanzen
                    derselben Domain als zwei identische Zeilen da — „bryve.app"
                    und „bryve.app" —, und welche davon die Demo war, stand
                    nirgends. Die Domain bleibt der kräftige Teil: Sie ist das,
                    wonach jemand sucht; die Subdomain sagt nur, welche von den
                    dreien.
                  */}
                  {tabelle.zeigt('domain') && <td className="px-4 py-3 text-slate-700 font-mono text-xs">
                    {inst.domain ? (
                      <>
                        {inst.subdomain && (
                          <span className="text-slate-400">{inst.subdomain}.</span>
                        )}
                        {inst.domain}
                      </>
                    ) : (
                      inst.subdomain ?? '—'
                    )}
                  </td>}
                  {tabelle.zeigt('status') && (
                    <td className="px-4 py-3">
                      <StatusBadge status={inst.status} type="instance" />
                    </td>
                  )}
                  {tabelle.zeigt('server') && <td className="px-4 py-3 text-slate-500 text-xs">
                    {inst.hostingMode === 'OBSERVED' ? (
                      // Beobachtet: läuft auf unserem Host, ausgerollt aber von
                      // Portainer. Der Servername ist die eigentliche Auskunft
                      // ("was läuft wo"), das Schild daneben sagt, warum hier
                      // keine Knöpfe stehen.
                      <span className="inline-flex items-center gap-1">
                        <span
                          className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-500"
                          title="Läuft auf unserer Infrastruktur, wird aber von Portainer ausgerollt. Der PC schaut nur zu."
                        >
                          beobachtet
                        </span>
                        {inst.server && <FarbPunkt farbe={inst.server.farbe} titel={inst.server.hostname} />}
                        {inst.server?.hostname ?? '—'}
                      </span>
                    ) : inst.hostingMode === 'SELF_HOSTED' ? (
                      // No server row exists for customer-owned hardware — the
                      // useful signal there is whether it is still calling home.
                      <span
                        className="inline-flex items-center gap-1"
                        title={
                          inst.lastHeartbeatAt
                            ? `Letzter Kontakt: ${new Date(inst.lastHeartbeatAt).toLocaleString('de-DE')}`
                            : 'Hat sich noch nie gemeldet'
                        }
                      >
                        <span className="rounded bg-violet-50 px-1.5 py-0.5 font-medium text-violet-700">
                          Fremd
                        </span>
                        <span
                          className={
                            !inst.lastHeartbeatAt
                              ? 'text-slate-400'
                              : Date.now() - new Date(inst.lastHeartbeatAt).getTime() <= 36 * 3600_000
                                ? 'text-emerald-600'
                                : 'text-red-600'
                          }
                        >
                          {inst.lastHeartbeatAt
                            ? formatDistanceToNow(new Date(inst.lastHeartbeatAt), { addSuffix: true })
                            : 'kein Kontakt'}
                        </span>
                      </span>
                    ) : inst.server ? (
                      // Dieselbe Farbe wie in der Serverliste — so findet das
                      // Auge die Instanzen eines Hosts ohne zu lesen.
                      <span className="inline-flex items-center gap-2">
                        <FarbPunkt farbe={inst.server.farbe} titel={inst.server.hostname} />
                        {inst.server.hostname}
                      </span>
                    ) : (
                      inst.serverId?.slice(0, 8) ?? '—'
                    )}
                  </td>}
                  {tabelle.zeigt('stack') && (
                    <td className="px-4 py-3">
                      <StackZelle eintraege={inst.techStack} am={inst.techStackAt} />
                    </td>
                  )}
                  {tabelle.zeigt('umgebung') && (
                    <td className="px-4 py-3">
                      <UmgebungsMarke wert={inst.environment} />
                    </td>
                  )}
                  {tabelle.zeigt('deployed') && (
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {inst.deployedAt
                        ? formatDistanceToNow(new Date(inst.deployedAt), { addSuffix: true })
                        : '—'}
                    </td>
                  )}
                  {tabelle.zeigt('geaendert') && (
                    <td className="px-4 py-3 text-xs">
                      <AenderungsZelle aenderung={inst.letzteAenderung} />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => navigate(`/instances/${inst.id}`)}
                        title="View"
                        className="p-1 text-brand-600 hover:text-brand-800"
                      >
                        <Eye size={14} />
                      </button>
                      {/* Nur wo der PC auch ausrollt.
                          Bei OBSERVED gehört der Stack Portainer. Ein Stoppknopf
                          hier würde etwas anfassen, wovon der PC nichts weiß —
                          und schlimmer: er verspräche eine Kontrolle, die es
                          nicht gibt. Sichtbar sein und steuerbar aussehen sind
                          zwei verschiedene Dinge. */}
                      {inst.hostingMode !== 'OBSERVED' && (
                        <>
                          <button
                            onClick={() => startMutation.mutate(inst.id)}
                            title="Start"
                            className="p-1 text-emerald-600 hover:text-emerald-800 disabled:opacity-40"
                            disabled={startMutation.isPending}
                          >
                            <Play size={14} />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('Stop this instance?')) stopMutation.mutate(inst.id);
                            }}
                            title="Stop"
                            className="p-1 text-amber-600 hover:text-amber-800 disabled:opacity-40"
                            disabled={stopMutation.isPending}
                          >
                            <Square size={14} />
                          </button>
                          <button
                            onClick={() => restartMutation.mutate(inst.id)}
                            title="Restart"
                            className="p-1 text-slate-500 hover:text-slate-700 disabled:opacity-40"
                            disabled={restartMutation.isPending}
                          >
                            <RotateCcw size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Blaetterleiste blaettern={tabelle.blaettern} />
    </div>
  );
}
